import React, { useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import {
    AuthCard,
    FieldGroup,
    InputFull,
    PasswordFieldWrapper,
    PasswordToggleButton,
    ActionsWrapper,
} from './AuthCard';
import { PrimaryButton } from '../../../shared/ui/Inputs';
import { Spinner } from '../../../shared/ui/Spinners';
import styled, { css } from 'styled-components';
import { GreenAlert, PrimaryTextColor, BoldTextColor } from '../../../app/theme/tokens/colors';
import { UserShouldRedefineHisPword } from '../../../shared/messages/errors';

const InfoBox = styled.div`
  background: ${GreenAlert};
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 12px;
  font-size: 14px;
  color: ${PrimaryTextColor};
  text-align: center;
`;

const RulesTitle = styled.p`
  margin: 2px 0 4px 0;
  font-size: 12px;
  color: ${BoldTextColor};
`;

const PasswordRules = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const RuleItem = styled.li<{ valid: boolean }>`
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 4px;
  border-radius: 6px;
  transition: color 0.25s ease;

  ${({ valid }) =>
        valid
            ? css`
          color: #4a9e55;
        `
            : css`
          color: ${PrimaryTextColor};
          opacity: 0.7;
        `}
`;

const RuleIcon = styled.span<{ valid: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  font-size: 8px;
  flex-shrink: 0;
  transition: border-color 0.25s ease, color 0.25s ease;

  ${({ valid }) =>
        valid
            ? css`
          border: 1.5px solid #4a9e55;
          color: #4a9e55;
        `
            : css`
          border: 1.5px solid #cbd5e0;
          color: transparent;
        `}
`;

const ValidationMessage = styled.p`
  margin: 8px 0 0 0;
  font-size: 12px;
  color: ${BoldTextColor};
`;

type Props = {
    email: string;
    newPassword: string;
    message: string;
    isLogin: boolean;
    showNewPassword: boolean;
    onChangeEmail: (v: string) => void;
    onChangeNewPassword: (v: string) => void;
    onToggleShowPassword: () => void;
    onSubmit: () => void;
};

export const RedefinePasswordCard: React.FC<Props> = ({
    email,
    newPassword,
    message,
    isLogin,
    showNewPassword,
    onChangeEmail,
    onChangeNewPassword,
    onToggleShowPassword,
    onSubmit,
}) => {
    const [showRulesAfterInvalidSubmit, setShowRulesAfterInvalidSubmit] =
        useState(false);

    const passwordChecks = {
        minLength: newPassword.length >= 8,
        upperCase: /[A-Z]/.test(newPassword),
        lowerCase: /[a-z]/.test(newPassword),
        number: /\d/.test(newPassword),
        special: /[^A-Za-z0-9]/.test(newPassword),
    };

    const isPasswordValid = Object.values(passwordChecks).every(Boolean);
    const shouldShowRules = showRulesAfterInvalidSubmit && !isPasswordValid;

    const isRedefineAccessNotice = message === UserShouldRedefineHisPword;
    const infoMessage = isRedefineAccessNotice ? message : '';
    const errorMessage = isRedefineAccessNotice ? '' : message;

    const rules = [
        {
            key: 'minLength',
            label: 'Mínimo de 8 caracteres',
            valid: passwordChecks.minLength,
        },
        {
            key: 'upperCase',
            label: 'Letra maiúscula',
            valid: passwordChecks.upperCase,
        },
        {
            key: 'lowerCase',
            label: 'Letra minúscula',
            valid: passwordChecks.lowerCase,
        },
        { key: 'number', label: 'Número', valid: passwordChecks.number },
        {
            key: 'special',
            label: 'Caractere especial',
            valid: passwordChecks.special,
        },
    ];

    const handleSubmit = () => {
        if (!isPasswordValid) {
            setShowRulesAfterInvalidSubmit(true);
            return;
        }

        setShowRulesAfterInvalidSubmit(false);
        onSubmit();
    };

    return (
        <AuthCard
            title="Bem-vindo ao"
            subtitle="Redefinir senha de acesso"
            message={errorMessage}
        >
            {infoMessage && <InfoBox>{infoMessage}</InfoBox>}

            <FieldGroup>
                <InputFull
                    placeholder="E-mail"
                    value={email}
                    onChange={(e) => onChangeEmail(e.target.value)}
                />

                <PasswordFieldWrapper>
                    <InputFull
                        type={showNewPassword ? 'text' : 'password'}
                        placeholder="Nova senha"
                        value={newPassword}
                        onChange={(e) => onChangeNewPassword(e.target.value)}
                    />
                    <PasswordToggleButton
                        type="button"
                        onClick={onToggleShowPassword}
                        aria-label={showNewPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                        {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                    </PasswordToggleButton>
                </PasswordFieldWrapper>

                {shouldShowRules && (
                    <div>
                        <RulesTitle>A senha deve conter:</RulesTitle>
                        <PasswordRules>
                            {rules.map(({ key, label, valid }) => (
                                <RuleItem key={key} valid={valid}>
                                    <RuleIcon valid={valid}>{valid ? '✓' : ''}</RuleIcon>
                                    {label}
                                </RuleItem>
                            ))}
                        </PasswordRules>
                        <ValidationMessage>
                            Sua senha não atende aos requisitos mínimos de segurança.
                        </ValidationMessage>
                    </div>
                )}
            </FieldGroup>

            {isLogin ? (
                <div
                    style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}
                >
                    <Spinner />
                </div>
            ) : (
                <ActionsWrapper>
                    <PrimaryButton onClick={handleSubmit}>Redefinir senha</PrimaryButton>
                </ActionsWrapper>
            )}
        </AuthCard>
    );
};
